import type {
  CreateCmmdSavedDataViewParams,
  DeleteCmmdSavedDataViewParams,
  UpdateCmmdSavedDataViewParams,
} from "@cmmd-forge/contracts";
import { createCmmdApiClient, CmmdApiError } from "../environments/cmmdApi";
import {
  fromCmmdSavedDataView,
  toCmmdSavedDataViewDefinition,
  type DataViewSurface,
  type SavedDataView,
} from "./viewDefinition";
import {
  clearLegacySavedDataViews,
  readLegacySavedDataViews,
  type LegacySavedDataView,
} from "./viewLocalDraftStore";

export function savedViewsQueryKey(surface: DataViewSurface, projectId: number | undefined) {
  return ["cmmd", "saved-data-views", surface, projectId ?? "global"] as const;
}

export function savedViewScope(surface: DataViewSurface, projectId: number | undefined) {
  return {
    surface,
    ...(surface === "cmmd.project.tasks" && projectId !== undefined ? { projectId } : {}),
  } as const;
}

export function savedViewErrorMessage(error: unknown): string {
  if (isSavedViewConflict(error)) {
    return "This view changed on another device. Canonical data was refreshed; review it and try again.";
  }
  return error instanceof Error ? error.message : "Saved views are temporarily unavailable.";
}

export function isSavedViewConflict(error: unknown): boolean {
  return error instanceof CmmdApiError && error.status === 409;
}

async function importLegacyViews(input: {
  readonly surface: DataViewSurface;
  readonly projectId?: number;
  readonly views: readonly LegacySavedDataView[];
}): Promise<void> {
  const api = createCmmdApiClient();
  for (const view of input.views) {
    await api.importSavedDataView({
      ...savedViewScope(input.surface, input.projectId),
      legacyViewId: view.id,
      name: view.name,
      definition: toCmmdSavedDataViewDefinition(input.surface, view.state),
      isDefault: view.isDefault,
      legacyCreatedAt: view.createdAt,
      legacyUpdatedAt: view.updatedAt,
    });
  }
}

export async function loadSavedViews(
  surface: DataViewSurface,
  projectId: number | undefined,
): Promise<readonly SavedDataView[]> {
  const api = createCmmdApiClient();
  const canonical = await api.listSavedDataViews(savedViewScope(surface, projectId));
  if (surface !== "cmmd.projects") return canonical.map(fromCmmdSavedDataView);

  const legacy = readLegacySavedDataViews(surface);
  if (legacy.length === 0) return canonical.map(fromCmmdSavedDataView);

  await importLegacyViews({
    surface,
    ...(projectId !== undefined ? { projectId } : {}),
    views: legacy,
  });
  clearLegacySavedDataViews(surface);
  return (await api.listSavedDataViews(savedViewScope(surface, projectId))).map(
    fromCmmdSavedDataView,
  );
}

export function upsertSavedView(
  views: readonly SavedDataView[] | undefined,
  next: SavedDataView,
): readonly SavedDataView[] {
  const retained: SavedDataView[] = [];
  for (const view of views ?? []) {
    if (view.id === next.id) continue;
    retained.push(next.isDefault && view.isDefault ? { ...view, isDefault: false } : view);
  }
  return [...retained, next].toSorted((left, right) =>
    left.createdAt.localeCompare(right.createdAt),
  );
}

export type SavedViewMutation =
  | { readonly kind: "create"; readonly params: CreateCmmdSavedDataViewParams }
  | {
      readonly kind: "update";
      readonly viewId: string;
      readonly params: UpdateCmmdSavedDataViewParams;
    }
  | {
      readonly kind: "delete";
      readonly viewId: string;
      readonly params: DeleteCmmdSavedDataViewParams;
    };

export type SavedViewMutationResult =
  | { readonly kind: "upsert"; readonly view: SavedDataView }
  | { readonly kind: "delete"; readonly viewId: string };

export async function mutateSavedView(input: SavedViewMutation): Promise<SavedViewMutationResult> {
  const api = createCmmdApiClient();
  if (input.kind === "create") {
    return {
      kind: "upsert",
      view: fromCmmdSavedDataView(await api.createSavedDataView(input.params)),
    };
  }
  if (input.kind === "update") {
    return {
      kind: "upsert",
      view: fromCmmdSavedDataView(await api.updateSavedDataView(input.viewId, input.params)),
    };
  }
  await api.deleteSavedDataView(input.viewId, input.params);
  return { kind: "delete", viewId: input.viewId };
}
